/* eslint-disable react/no-unescaped-entities */
import { ReactElement } from 'react';
import '../IncrementalityPlaybook.scss';
import './UserLevelCalculator.scss';
import { Img } from '@Components/Img';
import { INC_CDN, IncPlaybookSectionBox, PAGE_PREFIX } from '../IncrementalityPlaybookShared';

const PAGE_TITLE = 'User Level Calculator';

const createDefinition = (
  term: string,
  definition: string | ReactElement,
  recommendations?: string | ReactElement
) => (
  <>
    <div className='basic-text'>
      <u>{term}:</u> {typeof definition === 'string' ? <div>{definition}</div> : definition}
    </div>
    {recommendations && (
      <ul className='basic-list'>
        <li> {recommendations}</li>
      </ul>
    )}
  </>
);

export const UserLevelCalculator = () => (
  <div className='inc-playbook-page'>
    <IncPlaybookSectionBox
      content={
        <div className='basic-section'>
          <div className='basic-text'>
            This user level power calculator will provide sample size calculations for prospective
            incrementality tests and an estimate for the required spend to reach statistical
            significance.
          </div>
          <br />
          <div className='basic-text'>
            This calculator will take in inputted assumptions and will calculate a recommended spend
            based off of those assumptions. For the statistical parameters, it's advisable to stick
            with the default values as altering them can substantially impact the recommendations.
            Definitions for all the inputs and instructions for filling out the calculator are
            provided below.
          </div>
          <br />
          <div className='basic-text'>
            Most platforms offer ghost bidding which provides a no cost control group, so the
            calculator will assume 0 cost to the control group.
          </div>
          <br />
          <div className='basic-text'>
            When speccing out an incrementality test it is best to include safeguards against a test
            that fails to detect results. By default we include a safeguard by designing the test
            for significance level of 99%, when most clients will accept 90-95% significance levels
            as indicative of conclusive results. Secondly, we apply a high statistical power target,
            which reflects a higher probability of detecting smaller differences between the
            Treatment and Control groups when they are truly present -- this yields more reliable
            results and requires larger sample sizes.
          </div>
        </div>
      }
      pageTitle={PAGE_TITLE}
      title='Overview'
    />
    <IncPlaybookSectionBox
      content={
        <div className='basic-section'>
          {createDefinition('KPI Name', 'Free text the name of the KPI being measured in the test')}
          <br />
          {createDefinition(
            'Response Rate/ 1000 Impressions',
            'The number of conversions per 1000 impressions served. This can be pulled from the BlissPoint app. Use the performance tab for the desired channel, and select the desired KPI. Use the value under Conversions/Imps.'
          )}
          <br />
          {createDefinition(
            'Significance Level',
            <div>
              The chosen threshold that affects the likelihood of concluding that results are not
              due to chance. It determines the probability of making a Type I error, which is
              rejecting a true null hypothesis. It helps you decide how certain you want to be that
              the observed results are not random. <br />
              We highly recommend going with the default significance level of 0.99.
            </div>
          )}
          <br />
          {createDefinition(
            'Incrementality Level',
            <div>
              There is some underlying/background response rate that would've happened whether or
              not we advertised or not. Since our hypothesized response rate is based on historical
              attribution data, we want to account for how many of those users would have converted
              anyway and how many are incremental. This value should be between 0 and 1 to represent
              an incrementality percentage. For more info on incrementality, please visit:{' '}
              <a href={`${PAGE_PREFIX}/Incrementality101`}>Incrementality 101</a>.
            </div>
          )}
          <br />
          {createDefinition('Test Length (Days)', 'The number of days the test will run for.')}
          <br />
          {createDefinition(
            'CPM',
            'The base CPM (ex. fees) for the network(s) where the campaign is running.'
          )}
          <br />
          {createDefinition(
            'Control Percent',
            <div>
              The percentage of impressions that will be going towards the control group, PSAs or
              ghost bidding. By increasing the percentage allocated towards control (up to 50%), you
              can increase the power of the test/reduce the number of impressions needed to serve to
              reach statistical significance and thus reduce the overall cost of the test.
              <br />
              Note that certain ghost bidding options and platforms limit control allocation to a
              specific percentage so we recommended double checking on required allocations by the
              platform before finalizing the test.
            </div>
          )}
          <br />
          {createDefinition(
            'Cross Contamination',
            <div>
              In evaluating the data following the test, we need to exclude from the Control group
              anyone who did see a client advertisement, either due to test network leakage or due
              to running on other networks/platforms during the test. The cross-contamination
              percentage is an estimate of the percentage of the Control group that will need to be
              removed due to this leakage.
              <br />
              As a default, we recommend using 30% as a base level here. This figure can be raised
              if the Client is running on a significant number of additional non-test networks.
              <br />
              If this is an in-platform test this should be set to 0%.
            </div>
          )}
          <br />
          {createDefinition(
            'Power',
            <div>
              The probability of correctly concluding that an effect or difference exists when it
              truly does. In marketing, it helps determine how likely you are to detect a real
              impact from your advertising efforts. Higher power means a better chance of not
              missing important insights in your marketing data.
              <br />
              We highly recommend going with <b>the default power level of 0.8</b>.
            </div>
          )}
          <br />
          {createDefinition(
            'Residential Delivery Rate',
            <div>
              When evaluating the data after the test, we need to be able to match users with
              purchases. Non-residential ips need to be filtered out to ensure that users are
              cleanly matched based on if they were served an ad or not.
              <br />
              If this is an in-platform test this should be set to 100%.
            </div>
          )}
          <br />
          {createDefinition(
            'Average Frequency',
            'This represents how many impressions we expect users to receive on average during the test period, which will be used to back into the required number of impressions needed for the test altogether. The best way to get this is to look at historical campaigns or network data.'
          )}
          <br />
          {createDefinition(
            'CAPI',
            <div>
              For in platform tests we need to account for the possible signal loss due to privacy
              regulations. If the test is run in-platform check the box signifying if the client has
              CAPI set up or not.
              <br />
              For streaming/ off platform tests select not applicable.
              <br />
              If your client does not have CAPI set up on the platform, we highly recommend that you
              set it up for better tracking capabilities.
            </div>
          )}
          <br />
          {createDefinition(
            'Test Type',
            'Specify if the test will be a PSA or a Ghost bidding test. This determines if there will be spend in the control group or not.'
          )}
        </div>
      }
      id='field-definitions'
      title='Field Definitions'
    />
    <IncPlaybookSectionBox
      content={
        <div className='basic-section'>
          <div className='basic-header'>Vectorized Calculator</div>
          <div className='basic-text'>
            The purpose of this calculator is to allow you to spec out a test with multiple
            variations to more easily determine the best setup. For each variant of parameter
            combinations, fill out the parameters and click Add Data. You will see a new row of data
            in a table to the right. This can be repeated as many times as you would like. Once you
            have added all of the variants, click <b>Calculate test results</b>. This will calculate
            the test cost for each variant. You can then click <b>Download Results</b>, and it will
            download a CSV with the inputs and the results for further use.
          </div>
          <br />
          <div className='basic-header'>Multi Calculator</div>
          <div className='basic-text'>
            This calculator is used to spec out test where there are two treatment cells. The first
            section to fill out is the whole test inputs. This defines parameters that are constant
            across the cells. Then fill out the cell-specific parameters. Once all parameters are
            filled out, click <b>Calculate</b>, and the test spec will run and produce a table. You
            can then click <b>Download Results</b> to download a CSV for further use.
          </div>
          <br />
          <Img src={`${INC_CDN}/Streaming_Calculator.png`} />
        </div>
      }
      id='filling-out-the-user-level-calculator'
      title='Filling Out the User Level Calculator'
    />
    <IncPlaybookSectionBox
      content={
        <div className='user-level-calculator-container'>
          <iframe
            allowFullScreen
            className='user-level-calculator'
            id='userLevelCalculator'
            name='userLevelCalculator'
            sandbox='allow-scripts allow-popups allow-forms allow-same-origin allow-popups-to-escape-sandbox allow-downloads allow-storage-access-by-user-activation'
            src='https://bppm.shinyapps.io/user_level_power_calcs/'
            title='userLevelCalculator'
          />
        </div>
      }
      id='user-level-calculator'
      title='User Level Calculator'
    />
  </div>
);
